import type { CongratsDesign } from './congratsDefaults';

interface CongratsPreviewProps {
  design: CongratsDesign;
  mode: 'desktop' | 'mobile';
  /** 0–100 remaining until auto-close (for preview animation) */
  progressPercent?: number;
  title?: string;
  subtitle?: string;
  /**
   * `card` — compact thumbnail for Widgets list (fills the strip cleanly).
   * `full` — editor / large preview (default).
   */
  variant?: 'full' | 'card';
}

/** Live preview of the Cart Success Notification Bar */
export function CongratsPreview({
  design,
  mode,
  progressPercent = 72,
  title,
  subtitle,
  variant = 'full',
}: CongratsPreviewProps) {
  const isMobile = mode === 'mobile';
  const isCard = variant === 'card';
  const pct = Math.min(100, Math.max(0, progressPercent));
  const titleText = title || design.title;
  const subText = subtitle !== undefined ? subtitle : design.subtitle;
  const isBottom = design.position !== 'top';

  const toast = (
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: isCard ? '100%' : isMobile ? '100%' : 400,
        background: design.backgroundColor,
        color: design.textColor,
        borderRadius: design.borderRadius,
        boxShadow: isCard
          ? '0 8px 24px rgba(0,0,0,.14)'
          : '0 12px 36px rgba(0,0,0,.16)',
        padding: isCard ? '12px 14px 10px' : '14px 16px 12px',
        fontFamily: design.fontFamily,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: isCard ? 10 : 12,
        }}
      >
        <div
          style={{
            width: isCard ? 36 : 40,
            height: isCard ? 36 : 40,
            borderRadius: 12,
            background: `${design.accentColor}18`,
            border: `1.5px solid ${design.accentColor}40`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isCard ? 18 : 20,
            flexShrink: 0,
          }}
        >
          🎁
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: isCard ? 14 : 15,
              fontWeight: 700,
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
            }}
          >
            {titleText}
          </div>
          {subText ? (
            <div
              style={{
                marginTop: 2,
                fontSize: isCard ? 12 : 13,
                color: design.mutedColor,
                lineHeight: 1.35,
              }}
            >
              {subText}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          aria-label="Close"
          style={{
            border: 'none',
            background: 'transparent',
            color: design.mutedColor,
            fontSize: 18,
            lineHeight: 1,
            cursor: 'default',
            padding: 0,
            width: 24,
            height: 24,
          }}
        >
          ×
        </button>
      </div>

      {design.showCloseProgress && (
        <div
          style={{
            marginTop: isCard ? 10 : 12,
            height: 3,
            borderRadius: 99,
            background: 'rgba(0,0,0,.08)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              borderRadius: 99,
              background: design.accentColor,
              transition: 'width .2s linear',
            }}
          />
        </div>
      )}
    </div>
  );

  // Compact card thumbnail — toast centered on soft storefront chrome
  if (isCard) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 160,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 18px',
          background:
            'linear-gradient(165deg, #eef1f4 0%, #e4e8ec 45%, #dce1e6 100%)',
          fontFamily: design.fontFamily,
        }}
      >
        {/* Soft page mock */}
        <div
          style={{
            position: 'absolute',
            left: 28,
            right: 28,
            top: 22,
            bottom: 22,
            borderRadius: 10,
            background:
              'linear-gradient(180deg, #fafbfc 0%, #f0f2f4 55%, #e8ebef 100%)',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.04)',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          {toast}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: isMobile ? 20 : 16,
        overflow: 'hidden',
        background: isMobile
          ? 'linear-gradient(180deg, #e8ecef 0%, #d5dbe0 100%)'
          : 'linear-gradient(180deg, #eef0f2, #e2e5e8)',
        minHeight: isMobile ? 360 : 320,
        display: 'flex',
        alignItems: isBottom ? 'flex-end' : 'flex-start',
        justifyContent: 'center',
        padding: isMobile ? 16 : 24,
        fontFamily: design.fontFamily,
      }}
    >
      {!isMobile && (
        <div
          style={{
            position: 'absolute',
            inset: 16,
            borderRadius: 12,
            background:
              'linear-gradient(180deg, #f7f8f9 0%, #eceef0 40%, #e4e7ea 100%)',
            opacity: 0.95,
            zIndex: 0,
          }}
        />
      )}
      {toast}
    </div>
  );
}
